
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TerraceFormValues, terraceFormSchema, DayOfWeek } from '@/components/terrace-submission/types';

export const useTerraceForm = (defaultValues?: Partial<TerraceFormValues>) => {
  const form = useForm<TerraceFormValues>({
    resolver: zodResolver(terraceFormSchema),
    defaultValues: {
      name: "",
      type: "",
      address: "",
      // Add the default value for googleMapsUrl
      googleMapsUrl: "",
      openingHours: "",
      detailedOpeningHours: {
        monday: { isOpen: true, from: "", to: "" },
        tuesday: { isOpen: true, from: "", to: "" },
        wednesday: { isOpen: true, from: "", to: "" },
        thursday: { isOpen: true, from: "", to: "" },
        friday: { isOpen: true, from: "", to: "" },
        saturday: { isOpen: true, from: "", to: "" },
        sunday: { isOpen: true, from: "", to: "" },
      },
      terraceDescription: "",
      website: "",
      phone: "",
      hasFood: false,
      servesAlcohol: false,
      petFriendly: false,
      wheelchairAccessible: false,
      servesWine: false,
      servesBeer: false,
      allowsDogs: false,
      orientation: "unknown",
      sunStatus: "not-sunny",
      ...defaultValues
    }
  });

  // Helper function to parse detailed opening hours from backend response
  const parseDetailedOpeningHours = (detailedHours: any) => {
    if (!detailedHours) return undefined;
    
    const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const parsedHours: any = {};
    
    days.forEach(day => {
      if (detailedHours[day]) {
        parsedHours[day] = {
          isOpen: detailedHours[day].isOpen !== false,
          from: detailedHours[day].from || "",
          to: detailedHours[day].to || "",
        };
      } else {
        parsedHours[day] = { isOpen: true, from: "", to: "" };
      }
    });
    
    return parsedHours;
  };

  // Helper function to parse opening hours and extract days (legacy support)
  const parseOpeningDays = (openingHours: string): string[] => {
    if (!openingHours) return [];
    
    const days = [];
    const lowerHours = openingHours.toLowerCase();
    
    // Check for each day of the week
    if (lowerHours.includes('monday') || lowerHours.includes('mon')) days.push('monday');
    if (lowerHours.includes('tuesday') || lowerHours.includes('tue')) days.push('tuesday');
    if (lowerHours.includes('wednesday') || lowerHours.includes('wed')) days.push('wednesday');
    if (lowerHours.includes('thursday') || lowerHours.includes('thu')) days.push('thursday');
    if (lowerHours.includes('friday') || lowerHours.includes('fri')) days.push('friday');
    if (lowerHours.includes('saturday') || lowerHours.includes('sat')) days.push('saturday');
    if (lowerHours.includes('sunday') || lowerHours.includes('sun')) days.push('sunday');
    
    // If no specific days found but there's content, assume daily
    if (days.length === 0 && openingHours.trim()) {
      return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    }
    
    return days;
  };

  // Method to populate form from API data
  const populateFromApiData = (data: any) => {
    // Set basic form values
    form.setValue("name", data.name);
    form.setValue("address", data.address);

    // Populate googleMapsUrl if present in API response
    if (data.originalMapsUrl) {
      form.setValue("googleMapsUrl", data.originalMapsUrl);
    } else if (data.googleMapsUrl) {
      form.setValue("googleMapsUrl", data.googleMapsUrl);
    } else if (data.mapsUrl) {
      form.setValue("googleMapsUrl", data.mapsUrl);
    }

    // Handle detailed opening hours if provided
    if (data.detailedOpeningHours) {
      const parsedDetailedHours = parseDetailedOpeningHours(data.detailedOpeningHours);
      if (parsedDetailedHours) {
        form.setValue("detailedOpeningHours", parsedDetailedHours);
      }
    }
    
    // Legacy opening hours support
    if (data.openingHours) {
      form.setValue("openingHours", data.openingHours);
      const extractedDays = parseOpeningDays(data.openingHours);
      form.setValue("openingDays", extractedDays);
    }
    
    if (data.terraceDescription) {
      form.setValue("terraceDescription", data.terraceDescription);
    }
    
    if (data.website) {
      form.setValue("website", data.website);
    }
    
    if (data.phone) {
      form.setValue("phone", data.phone);
    }
    
    // Auto-calculate servesAlcohol based on wine/beer detection
    const detectedWine = data.servesWine || false;
    const detectedBeer = data.servesBeer || false;
    const detectedAlcohol = data.servesAlcohol || detectedWine || detectedBeer;
    
    form.setValue("hasFood", data.hasFood || false);
    form.setValue("servesAlcohol", detectedAlcohol);
    form.setValue("petFriendly", data.petFriendly || false);
    form.setValue("wheelchairAccessible", data.wheelchairAccessible || false);
    form.setValue("servesWine", detectedWine);
    form.setValue("servesBeer", detectedBeer);
    form.setValue("allowsDogs", data.allowsDogs || false);
    
    // Handle photo URL if provided
    if (data.photoUrl) {
      form.setValue("photo_url", data.photoUrl);
    }
    
    // Handle type field with proper value setting
    if (data.type) {
      form.setValue("type", data.type);
      // Trigger form validation to ensure the select dropdown updates
      form.trigger("type");
    }
  };

  // Method to reset form to default state
  const resetToDefaults = () => {
    form.reset({
      name: "",
      type: "",
      address: "",
      openingHours: "",
      openingDays: [],
      detailedOpeningHours: {
        monday: { isOpen: true, from: "", to: "" },
        tuesday: { isOpen: true, from: "", to: "" },
        wednesday: { isOpen: true, from: "", to: "" },
        thursday: { isOpen: true, from: "", to: "" },
        friday: { isOpen: true, from: "", to: "" },
        saturday: { isOpen: true, from: "", to: "" },
        sunday: { isOpen: true, from: "", to: "" },
      },
      terraceDescription: "",
      website: "",
      phone: "",
      hasFood: false,
      servesAlcohol: false,
      petFriendly: false,
      wheelchairAccessible: false,
      servesWine: false,
      servesBeer: false,
      allowsDogs: false,
      orientation: "unknown",
      sunStatus: "not-sunny",
      googleMapsUrl: "",
    });
  };

  return {
    form,
    populateFromApiData,
    resetToDefaults,
    parseDetailedOpeningHours,
    parseOpeningDays
  };
};
